/**
 * Root Error Boundary
 *
 * Purpose: Full-page fallback for a render-tree crash that escapes every
 *          other error boundary. BaseComponent.tsx's internal ErrorBoundary
 *          is scoped to a single widget (inline Alert) — this is the
 *          app-wide backstop mounted around <App/> in index.tsx. See
 *          ORCH-285.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';

interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  hasError: boolean;
}

export class RootErrorBoundary extends Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
  constructor(props: RootErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): RootErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Uncaught error in application render tree:', error, errorInfo);
  }

  private handleRefresh = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            gap: 2,
            p: 3,
          }}
        >
          <Typography variant="h5">
            Something went wrong and the page couldn&apos;t be displayed
          </Typography>
          <Button variant="contained" onClick={this.handleRefresh}>
            Refresh page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default RootErrorBoundary;
